import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { TabulatorFull as Tabulator } from "tabulator-tables";
import { useAppDispatch, useAppSelector } from '../../hooks/index';
import {
    Afr, DimensionReference, FactTable,
    generateLocalStateFromEventProperties,
    getDimensionForFact, getSelectedAfr, getTable,
    MatchTableFacts
} from '../../redux/AfrSlice';
import { getAccountId, loadConcept } from '../../redux/ConceptSlice';
import { addColumnFilterAccountWithoutFacts, ColumnFilterAccountWithoutFact, selectColumnFilterAccountsWithoutFacts, setIsDirty } from '../../redux/MiscSlice';
import { DimensionalAccountScope, getAccountFilterForFundGroup, selectScopeNetworks } from '../../redux/NetworkSlice';
import { ActiveTaxonomyVersionInfo, selectCurrentVersion, TaxonomyTypeEnum } from '../../redux/TaxonomyVersionSlice';
import { AdminUserRoleEnum, selectAdminUserRole } from '../../redux/UserSlice';
import { ErrorSummaryAFRSection, getErrorList } from '../../redux/ValidationSlice';
import enums from '../../utils/enums';
import utilityHelper from '../../utils/utilityHelper';
import SelectNewAccountModal from '../layout/Modal/SelectNewAccountModal';
import SelectSubAccountModal from '../layout/Modal/SelectSubAccountModal';



const AccountEditorGridBulk = (props: {
    tableName: string,
    gridData: any[],
    gridColumns: any[],
    gridOptions: any,
    cellEditHandler: any,
    doneFlag: string,
    noFlag: string,
    fundGroup: string | null,
    setHasData: (hasData: boolean) => void
}) => {

    /* ************************************** Redux ********************************************/
    const dispatch = useAppDispatch();
    const errorList = useAppSelector(getErrorList);
    const [deleteRequest, setDeleteRequest] = useState<MatchTableFacts | null>(null);
    var versionInfo: ActiveTaxonomyVersionInfo | null = useAppSelector(selectCurrentVersion);
    const [accountCodeFilter, setAccountCodeFilter] = useState<DimensionalAccountScope>({ filter: "", accountMappings: {} });
    const scopeNetworks = useAppSelector(selectScopeNetworks);
    const adminUserRole: AdminUserRoleEnum | null = useAppSelector(selectAdminUserRole);
    const fundGroupsWithoutFactsSelector = useAppSelector(selectColumnFilterAccountsWithoutFacts);

    /* ************************************** Modal section ************************************/
    const parameters = useLocation();
    const firstFieldId = "FG_001_GeneralFundMember";
    const [modalSubAccountIsOpen, setModalSubAccountIsOpen] = useState(false);
    const [modalAccountIsOpen, setModalAccountIsOpen] = useState(false);
    const [dataRow, setdataRow] = useState({});
    const [fieldToEdit, setFieldToEdit] = useState(firstFieldId);
    const [displayCode, setDisplayCode] = useState<string>("");
    const [hiddenColumnMap, setHiddenColumnMap] = useState<any>({});
    const isDoneRef = useRef(false);
    var tabulatorRef = React.useRef<any>();
    const currentAfr: Afr | null = useAppSelector(getSelectedAfr);
    isDoneRef.current = isDoneOrNo();
    

    function isDoneOrNo() {
        var eventDone = generateLocalStateFromEventProperties(currentAfr, [props.doneFlag]);
        var eventNo = generateLocalStateFromEventProperties(currentAfr, [props.noFlag]);
        let flag: boolean = (eventDone[props.doneFlag] ? true : false) || (eventNo[props.noFlag] ? true : false) || utilityHelper.checkStatusList(currentAfr, enums.verifiedCertifiedRecertified) || (adminUserRole === AdminUserRoleEnum.Reviewer || adminUserRole === AdminUserRoleEnum.Admin);

        // NOTE - for new AFRs these flags will be null.
        // Since there may be no assignment true or false, we check for null
        return flag ? flag : false;
    }
    function isDone() {
        var eventDone = generateLocalStateFromEventProperties(currentAfr, [props.doneFlag]);
        return eventDone[props.doneFlag];
    }
    function isNoData() {
        var isDone = true;
        var flag: boolean | null = null;
        var eventNo = generateLocalStateFromEventProperties(currentAfr, [props.noFlag]);
        return eventNo[props.noFlag] ? true : false;
    }


    const getEntityCodeFromUrl = function () {
        var returnValue = "";
        if (parameters && parameters.state && parameters.state.entityCode) { return parameters.state.entityCode }
        return returnValue;
    }

    const cleanup = function () {
        utilityHelper.saveDeferredChanges(dispatch);
    }

    /* ***********************************************************************************************************/
    //Events
    /* ***********************************************************************************************************/
    const handleCloseSubAccountModal = function (account: any) {
        if ((typeof account) === "string") {
            setFieldToEdit(firstFieldId);
        }
        setModalSubAccountIsOpen(false);
    }

    const handleCloseAccountModal = function (account: any) {
        if ((typeof account) === "string") {
            setFieldToEdit(firstFieldId);
        }
        setModalAccountIsOpen(false);
        //check if account is an account code, if its not don't do the next section
        
        if (typeof account === "string" && props.fundGroup) {
            let newFundGroupAccountWithoutFact: ColumnFilterAccountWithoutFact = { accountCode: account, columnFilter: props.fundGroup ? props.fundGroup : "" };
            if (fundGroupsWithoutFactsSelector.some((x: ColumnFilterAccountWithoutFact) => x.accountCode === account && x.columnFilter === newFundGroupAccountWithoutFact.columnFilter) == false) {
                dispatch(addColumnFilterAccountWithoutFacts(newFundGroupAccountWithoutFact));
            }
        }
    }


    const handleRowFormatter = function (row: any) {
        /* This event can be used to disable the parent row when any children exist
           This event is triggered each time a row is formatted/painted  */

        for (var cell of row.getCells()) {
            if (row._row.type != "row") {
                continue
            }

        }

    }

    const showErrorIcon = function (cell: any, formatterParams: any, onRendered: any) {
        var text: string = cell.getValue();
        var { section, tab } = utilityHelper.getSectionError(props.tableName);

        if ((typeof text) === "undefined") {
            return text;
        }

        // Verify if there is an error in this section
        var errorSection: ErrorSummaryAFRSection = errorList.find((x: any) => x.section.sectionCode === section);
        if ((typeof errorSection) === "undefined") {
            return text;
        }

        // Discover the array of errors for the section
        var error = errorSection.errorList.find(x => x.entityType === tab && x.entityCode === cell.getData().accountCode);

        if ((typeof error) === "undefined") {
            return text;
        } else {
            if (error !== undefined) {
                return "<i class='bi bi-x-octagon-fill text-danger' title='" + error.description + "'></i> " + text;
            }
        }
    };

    const handleHeaderClick = function (e: any, column: any) {
        // get the column name to pull the concept associated to it
        var fundCode = column.getField();
        dispatch(loadConcept({
            conceptId: fundCode,
            taxonomyType: currentAfr ? currentAfr.taxonomyType : TaxonomyTypeEnum.AFR,
            afrYear: currentAfr ? currentAfr.year : 2019
        }));

    }

    const handleCellClick = function (e: any, cell: any) {
        //e - the click event object
        //cell - cell component
        var field = cell.getField();
        var row = cell.getRow();

        if (row._row.type != "row") { return }
        if (row === undefined) {
            return;
        }
        //            row.scrollTo();
        var rowId = row.getData().id;
        var accountId: string | null = null;
        if (rowId) {
            accountId = getAccountId(rowId);
        } else {
            accountId = "AccountCode_" + row.getData().accountCode;
        }
        if (accountId) {
            dispatch(loadConcept({
                conceptId: accountId,
                taxonomyType: currentAfr ? currentAfr.taxonomyType : TaxonomyTypeEnum.AFR,
                afrYear: currentAfr ? currentAfr.year : 2022
            }));
        }

        if (field === "buttonhandler") {
            var parentRow = row.getTreeParent();
            if (!parentRow) {
                // Calling to modal screen to add a new row
                if (isDoneRef.current === false) {
                    e.stopPropagation();
                    cell.getTable().deselectRow();
                    cell.getRow().select();

                    setdataRow(row.getData());
                    setModalSubAccountIsOpen(true);
                }
            }
        }
    }

    // special logic to verify if a cell is editable or not
    // editable    --> return true
    // no editable --> return false
    const handleEditCheck = function (cell: any) {
        //cell - the cell component for the editable cell
        //parent rows are editable only if they don't have sub-accounts
        var row = cell.getRow()
        if (isDoneRef.current === true) return false;
        if (row._row.type != "row") return false;
        if (row === undefined) {
            return;
        }

        /* this code only runs if rowType = 'row' */
        var parentRow = row.getTreeParent();
        var children = row.getTreeChildren();

        if (!parentRow && children.length > 0) {
            return false;
        }
        return true;

    }


    const handleRowClick = function (e: any, row: any) {
        //e - the click event object
        //row - row component
        if (row._row.type != "row") { return }

        row.getTable().deselectRow();
        row.select();

        // This below code will trigger on hamgurger icon click event in tabulator grid data.
        const srcElmnt = e.srcElement.outerHTML;
        const tgtElement = "<div class=\"tabulator-row-handle-bar\"></div>";
        if (srcElmnt === tgtElement) {
            var button = document.createElement("div");
            button.innerHTML = "<span style='color:white;'><i class='bi bi-plus-circle' title='Add new Account...'></i></span>";
            var swalMessage: string | null = null;
            var dataSection = utilityHelper.getDataSection(props.fundGroup, props.tableName);
            if (isDoneOrNo() === false) {
                setdataRow(row.getData());
                setModalAccountIsOpen(true);
            } else if (isDone()) {
                swalMessage = "To enter " + dataSection + ", please de-select the Done box.";
            } else if (isNoData()) {
                swalMessage = "To enter " + dataSection + ", please de-select the No " + dataSection + " box.";
            } else {
                swalMessage = "This AFR is locked, you cannot enter " + dataSection + ".";
            }
            if (swalMessage) {
                Swal.fire(
                    "Cannot add accounts for " + dataSection,
                    swalMessage,
                    'info'
                );
            }
        }
    }


    // handle the logic to add a new account 
    const isEmptyColumn = function (tableDataRows: any[], fld: string) {
        for (var dataRow of tableDataRows) {
            if (!dataRow.hasOwnProperty(fld)) {
                continue;
            }
            var val = dataRow[fld];
            if (val == null) {
                continue;
            }
            return false;
        }
        return true;
    }
    const collapseEmptyColumns = function (table: any) {
        var colDefs: any[] = table.getColumnDefinitions();
        var tableRows: any[] = utilityHelper.flattenRowList(table.getData(), []);
        var hcm: any = {
        }
        if (colDefs) {
            colDefs.map(cd => {
                if (cd.hasOwnProperty("columns")) {
                    var cols: any[] = cd.columns;
                    cols.map(col => {
                        if (col.hasOwnProperty("validator") && isEmptyColumn(tableRows, col.field)) {
                            if (!hiddenColumnMap.hasOwnProperty(col.field)) {
                                // we are going to hide it
                                hcm[col.field] = col.field;
                            }
                            table.toggleColumn(col.field);
                        }
                    });
                }
            });
        }
        setHiddenColumnMap(hcm);
    }

    const collapseEmptyColumnsFormatter = function (cell: any, formatterParams: any, onRendered: any) {

        //build dropdown
        var button = document.createElement("div");

        button.innerHTML = "<i class='bi bi-filter-circle' title='Toggle collapse of empty columns...'></i><span> Show/Hide empty columns</span>";

        button.onclick = function () {
            collapseEmptyColumns(cell.getTable());
        }

        return button;
    }

    const plusAccountTitleFormatter = function (cell: any, formatterParams: any, onRendered: any) {

        //build dropdown
        var button = document.createElement("div");

        button.innerHTML = "<span style='color:white;'><i class='bi bi-plus-circle' title='Add new Account...'></i></span>";

        button.onclick = function () {
            // add code here
            var swalMessage: string | null = null;
            var dataSection = utilityHelper.getDataSection(props.fundGroup, props.tableName);
            if (isDoneOrNo() === false) {
                setdataRow(""); // clear the selected row if row was selected using hamburger icon
                setModalAccountIsOpen(true);
            } else if (isDone()) {
                swalMessage = "To enter " + dataSection + ", please de-select the Done box.";
            } else if (isNoData()) {
                swalMessage = "To enter " + dataSection + ", please de-select the No " + dataSection + " box.";
            } else {
                swalMessage = "This AFR is locked, you cannot enter " + dataSection + ".";
            }
            if (swalMessage) {
                Swal.fire(
                    "Cannot add accounts for " + dataSection,
                    swalMessage,
                    'info'
                );

            }
        }

        return button;
    }

    const removeRowTitleFormatter = function (cell: any, formatterParams: any, onRendered: any) {
        return "<span style='color:red'><i class='bi bi-dash-circle' title='Remove row'></i></span>";
    }

    // logic to handle the dirty flag in redux store
    const handleEdited = function (cell: any) {

        var newValue: string = cell.getValue();
        var rowData: any = cell.getRow().getData();
        var field: string = cell.getField();
        newValue = newValue.trim();
        var prevEffectiveValue: string = utilityHelper.getEffectiveStringValue(cell.getOldValue(), rowData, field);
        var newEffectiveValue: string = utilityHelper.getEffectiveStringValue(newValue, rowData, field);
        if ((prevEffectiveValue !== newEffectiveValue) || (newValue.length > 0 && utilityHelper.getEffectiveNumericValue(newValue) === 0)) {
            // Turn ON the dirty flag in redux
            dispatch(setIsDirty({ IsDirty: true, Page: "" }));
            utilityHelper.updateTableCell(props.tableName, props.fundGroup, cell, cell.getTable(), dispatch);
            props.setHasData(true);
        }

    }


    // ************************************************************* /*
    // General purpose functions                                     //
    // ************************************************************* /*



    // calculate totals for each row
    const getTotals = function (value: any, data: any, type: any, params: any, component: any) {
        let total = 0;

        if (data._children) { return "" }

        Object.getOwnPropertyNames(data).forEach(
            function (val, idx, array) {
                // Fetch the value associated with the property and try to convert to a number
                // NOTE - some of the row attributes are metadata, not fact values, so they need to be skipped
                switch (val) {
                    case "account":
                    case "accountId":
                    case "accountCode":
                    case "Total":
                        break;
                    default:
                        if (utilityHelper.getFloatValue(data[val]) != 0.0) {
                            total = total + utilityHelper.getFloatValue(data[val]);
                        }
                        break;
                }
            }
        );

        return total;
    }

    // ************************************************************* /*
    // Grid Options                                                  //
    // ************************************************************* /*
    const newGridOptions = {
        layout: "fitDataTable",
        layoutColumnsOnNewData: true,
        dataTree: true,
        dataTreeStartExpanded: true,
        dataTreeChildColumnCalcs: true,
        selectable: 1,
        selectableRollingSelection: false,
        scrollToRowPosition: "center",
        height: "630",  // to be adjusted if another thing is decided
        index: "accountCode",
        cellEdited: (cell: any) => handleEdited(cell),
        cellClick: (e: any, cell: any) => handleCellClick(e, cell),
        rowFormatter: (row: any) => handleRowFormatter(row),
        ...props.gridOptions
    }

    const visibleHandler: boolean = (!((props.tableName.includes("revenue") && !props.tableName.includes("revenue_"))
        || props.tableName.includes("balance")));

    const handleDeleteCellClick = function (event: any, cell: any) {
        event.stopPropagation();

        if (adminUserRole === AdminUserRoleEnum.Reviewer || adminUserRole === AdminUserRoleEnum.Admin) {
            return;
        }

        if (isDoneOrNo() === true) {
            alert("You must un-check the done flag to delete rows");
            return;
        }
        cleanup();

        var table = cell.getTable();
        if (!table) {
            return;
        }
        var row: any = cell.getRow();
        var rowData = row.getData();
        var accParts: string[] = rowData.accountCode.split("|");
        setDisplayCode(accParts[0]);
        var rowToSelect = row.getPrevRow();
        if (!rowToSelect) {
            rowToSelect = row.getNextRow();
        }
        var nextAccountCode = "";
        if (rowToSelect) {
            nextAccountCode = rowToSelect.getData().accountCode;
        }
        var rowMatch: MatchTableFacts = {
            tableName: props.tableName,
            matchConcept: {
                conceptId: rowData.accountId,
                dimensions: rowData.rowDimensions,
                conceptName: nextAccountCode,
                taxonomyType: utilityHelper.getTaxonomyTypeFromVersion(versionInfo),
                afrYear: currentAfr !== null ? currentAfr.year : 0
            }
        }
        setDeleteRequest(rowMatch);
       
    }

        const defaultColumns: any = [];
        var allowedColumnDimensions: any | null = null;
        if (props.fundGroup) {
            allowedColumnDimensions = {};
            newGridOptions.layout = "fitDataStretch";
        }

        defaultColumns.push(
            {
                title: "",
                headerHozAlign: "center",
                frozen: true,
                titleFormatter: collapseEmptyColumnsFormatter,
                columns: [
                    { rowHandle: true, formatter: "handle", field: "buttonhandler", headerSort: false, width: 45, minWidth: 45, visible: visibleHandler, editable: handleEditCheck },
                    { title: "Account Code", field: "account", width: 280, formatter: showErrorIcon },
                    { title: "Account Code", field: "action", width: 25, headerSort: false, cellClick: handleDeleteCellClick, formatter: removeRowTitleFormatter, titleFormatter: plusAccountTitleFormatter }
                ]
            }
        );


        var acctTable: FactTable | null = null;
        if (currentAfr) {
            acctTable = getTable(currentAfr, props.tableName);

            if (acctTable) {
                // Note - it is possible for the requested account table to not be present in this AFR
                // However that should be deal with upstream so the option doe snot appear
                for (var tbeGroup of acctTable.structure.nthlevelcolumnHierarchy) {
                    if (props.fundGroup && tbeGroup.label !== props.fundGroup) {
                        continue;
                    }
                    var t: string | null = null;
                    var tGroup = "";
                    var colGroup: any[] = [];
                    if (tbeGroup.sublevels) {
                        for (var tbeCol of tbeGroup.sublevels) {
                            if (tbeCol.dimensionaspect.member != null) {
                                t = tbeCol.dimensionaspect.member.id;
                                if (props.fundGroup) {
                                    // We only here if this column group has passed the filter.
                                    allowedColumnDimensions[t] = tbeCol;
                                }
                                newGridOptions.layout = "fitData";
                                var vis: boolean = true;
                                if (hiddenColumnMap.hasOwnProperty(t)) {
                                    vis = false;
                                }
                                var titletext = tbeCol.label.replace(" Funds", "").replace(" Fund", "");
                                colGroup.push({
                                    title: titletext,
                                    field: t,
                                    headerSort: false,
                                    editor: "input",
                                    visible: vis,
                                    width: 160,
                                    editable: handleEditCheck,
                                    headerClick: handleHeaderClick,
                                    editorParams: { mask: "*999999999999" },
                                    validator: "numeric",
                                    headerHozAlign: "right",
                                    topCalc: "sum",
                                    topCalcFormatter: "money",
                                    topCalcFormatterParams: { symbol: "$", precision: 0 },
                                    hozAlign: "right",
                                    formatter: "money", formatterParams: { symbol: "$", precision: 0 },
                                    cellEdited: handleEdited,
                                    cellClick: handleCellClick,
                                    mutateLink: "Total"
                                });
                            }
                        }
                        tGroup = tbeGroup.label;
                    } else {
                        if (tbeGroup.dimensionaspect.member != null) {
                            t = tbeGroup.dimensionaspect.member.id;
                            if (props.fundGroup) {
                                // We only here if this column group has passed the filter.
                                allowedColumnDimensions[t] = tbeGroup;
                            }
                            newGridOptions.layout = "fitData";
                            var vis: boolean = true;
                            if (hiddenColumnMap.hasOwnProperty(t)) {
                                vis = false;
                            }
                            var titletext = tbeGroup.label.replace(" Funds", "").replace(" Fund", "");
                            colGroup.push({
                                title: titletext,
                                field: t,
                                headerSort: false,
                                editor: "input",
                                visible: vis,
                                width: 160,
                                editable: handleEditCheck,
                                headerClick: handleHeaderClick,
                                editorParams: { mask: "*999999999999" },
                                validator: "numeric",
                                headerHozAlign: "right",
                                topCalc: "sum",
                                topCalcFormatter: "money",
                                topCalcFormatterParams: { symbol: "$", precision: 0 },
                                hozAlign: "right",
                                formatter: "money", formatterParams: { symbol: "$", precision: 0 },
                                cellEdited: handleEdited,
                                cellClick: handleCellClick,
                                mutateLink: "Total"
                            });
                        }
                    }
                    defaultColumns.push({
                        title: tGroup,
                        field: '',
                        headerHozAlign: "center",
                        columns: colGroup
                    });
                }
            }
        }

        // Adding Total Column
        defaultColumns.push(
            {
                title: 'Total',
                headerHozAlign: "center",
                field: 'Total',
                headerSort: false,
                hozAlign: 'right',
                editor: false,
                width: 200,
                mutator: getTotals,
                topCalc: "sum",
                topCalcFormatter: "money",
                topCalcFormatterParams: { symbol: "$", precision: 0 },
                formatter: "money", formatterParams: { symbol: "$", precision: 0 },
            }
        );

        var tabledata: any = [];
        if (acctTable) {
            var columnAxis = "FundGroupsAxis"; // Should derive from table structure
            var currentGroupRow: any | null = null;
            var currentGroupChildren: any[] = [];
            var currentGroupLabel: string | null = null;

            for (var row of acctTable.rows) {
                var rowid = "";
                var accountLabel = "";
                var accountId = "";
                var accountCode = "";
                if (row != null && row.account != null) {
                    if (row.account.code != null) {
                        accountCode = row.account.code;
                        if (accountCodeFilter && accountCodeFilter.filter &&
                            accountCodeFilter.filter
                                .split(";")
                                .filter(Boolean) // removes empty strings from trailing semicolon
                                .includes(accountCode)) {
                            // This account should be filtered from this table because its controlled by fund group and appears in the exclusion list
                            continue;
                        }
                        rowid = props.tableName + "|" + accountCode;
                    }
                    accountLabel = (row.account.name ? row.account.name : "");
                    accountId = (row.account.id ? row.account.id : "");
                }

                if (currentGroupLabel && accountLabel !== currentGroupLabel) {
                    // We have a pending group row to push
                    tabledata.push(currentGroupRow);
                    currentGroupRow = null;
                    currentGroupLabel = null;

                }
                var rdims: DimensionReference[] = [];
                var subcodelist = "";
                if (row.dimensions.length > 0) {
                    rdims = row.dimensions;
                    // Check to see if we have a group row already, and if not to make one
                    if (!currentGroupRow) {
                        currentGroupChildren = [];
                        currentGroupRow = { id: accountCode, rowConceptId: accountId, accountId: accountId, account: accountLabel, _children: currentGroupChildren, accountCode: accountCode };
                        currentGroupLabel = accountLabel;
                    } else {
                        currentGroupChildren = currentGroupRow._children;
                    }

                    var dimlist = "";

                    for (var dimref of row.dimensions) {
                        if (dimlist.length > 0) {
                            dimlist += ", ";
                            subcodelist += "|";
                        }
                        if (dimref.member != null) {
                            dimlist += dimref.member.name;
                            subcodelist += dimref.member.code;
                        }
                    }
                    accountLabel = dimlist;
                    if (subcodelist.length > 0) {
                        rowid += "|" + subcodelist
                    }
                }
                var nextRow: any = {
                    id: rowid, account: accountLabel, rowConceptId: accountId, accountId: accountId, accountCode: accountCode + "|" + subcodelist, rowDimensions: rdims
                }
                for (var fact of row.facts) {
                    var dimensionForFact = getDimensionForFact(fact, columnAxis);
                    if (dimensionForFact != null) {
                        var colcode = dimensionForFact.id;
                        if (allowedColumnDimensions) {
                            if (allowedColumnDimensions.hasOwnProperty(colcode)) {
                                nextRow[colcode] = fact.value;
                            }
                        } else {
                            nextRow[colcode] = fact.value;
                        }
                    }
                }
                if (currentGroupLabel) {
                    currentGroupChildren.push(nextRow);
                } else {
                    tabledata.push(nextRow);
                }

            }

            if (currentGroupLabel) {
                tabledata.push(currentGroupRow);
            }
        }

        useEffect(() => {
            if (props.fundGroup) {
                var scopeMapping: DimensionalAccountScope = getAccountFilterForFundGroup(props.fundGroup, scopeNetworks);
                setAccountCodeFilter(scopeMapping);
            }
        }, [props]);

        useEffect(() => {
            if (deleteRequest) {
                var dreq: MatchTableFacts = deleteRequest;
                setDeleteRequest(null);
                utilityHelper.showDeletePopup(currentAfr, dreq, displayCode, dispatch);
            }
        }, [deleteRequest]);

    React.useEffect(() => {

        var filteredRows: any[] = [];

        if (currentAfr && currentAfr.year > 2023) {

            let fundGroupValue = props.fundGroup ? props.fundGroup : null;

            let fundGroupMemberIds: Set<string> = utilityHelper.getFundGroupMemberIdSetFromAfr(currentAfr, fundGroupValue, props.tableName);
      
            //Filter each table data row's children.  Each child row either has a fact value assigned to it or has no fact but is a new row for that fund group

            for (var i = 0; i < tabledata.length; i++) {

                var tableRow = tabledata[i];
                var filteredTableRowChildren: any[] = [];

                if (tabledata[i]) {
                    for (var j = 0; j < tabledata[i]._children.length; j++) {
                        if (tabledata[i]._children[j] && ((Object.keys(tabledata[i]._children[j]).length > 6 && Object.keys(tabledata[i]._children[j]).some((key: string) => fundGroupMemberIds.has(key)))
                            || (fundGroupsWithoutFactsSelector && fundGroupsWithoutFactsSelector.some((accountFilterPair: ColumnFilterAccountWithoutFact) => accountFilterPair.accountCode == tabledata[i]._children[j].accountCode
                                && accountFilterPair.columnFilter == props.fundGroup)))) {
                            filteredTableRowChildren.push(tabledata[i]._children[j]);
                        }
                    }
                }

                if (filteredTableRowChildren.length > 0) {

                    tableRow._children = filteredTableRowChildren;
                    filteredRows.push(tableRow);
                }
            }

        } else {
            filteredRows = tabledata;
        }
         
            tabulatorRef.current = new Tabulator("#table", {
                data: filteredRows,
                columns: defaultColumns,
                layout: props.fundGroup ? "fitDataStretch" : "fitColumns",
                layoutColumnsOnNewData: true,
                dataTree: true,
                dataTreeStartExpanded: true,
                dataTreeChildColumnCalcs: true,
                selectable: 1,
                selectableRollingSelection: false,
                scrollToRowPosition: "center",
                height: "630",  // to be adjusted if another thing is decided
                index: "accountCode",
                rowFormatter: (row: any) => handleRowFormatter(row),

            }).on("rowClick", handleRowClick);

            return () => {
                cleanup();
            }

        }, [currentAfr]);

        return (
            <>
                <div id="table" ref={ele => { tabulatorRef.current = ele }} />

                {visibleHandler && (
                    <SelectSubAccountModal
                        tableName={props.tableName}
                        row={dataRow}
                        isOpen={modalSubAccountIsOpen}
                        onCloseModal={handleCloseSubAccountModal}
                        scopeMappings={accountCodeFilter}
                    />)}

                {acctTable && (<SelectNewAccountModal
                    tableName={props.tableName}
                    isOpen={modalAccountIsOpen}
                    row={dataRow}
                    onCloseModal={handleCloseAccountModal}
                    scopeMappings={accountCodeFilter}
                />)}

            </>


        );
    }
export default  AccountEditorGridBulk;
